import React, { useState } from 'react';
import { CreditCard, User, Calendar, Lock } from 'lucide-react';
interface PaymentFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  totalAmount: number;
}
export function PaymentForm({
  onSubmit,
  onCancel,
  totalAmount
}: PaymentFormProps) {
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Simple card type detection
  const getCardType = (number: string) => {
    const cleanNumber = number.replace(/\D/g, '');
    if (cleanNumber.startsWith('2')) return 'PayPak';
    if (cleanNumber.startsWith('4')) return 'Visa';
    if (cleanNumber.startsWith('5')) return 'Mastercard';
    if (cleanNumber.startsWith('6')) return 'UnionPay';
    return '';
  };
  const getCardLogo = (type: string): string | null => {
    switch (type) {
      case 'Visa':
        return 'https://cdn.magicpatterns.com/uploads/8dFj1ebd13MwAe3XqFYNrR/Visa_Logo.png';
      case 'Mastercard':
        return 'https://cdn.magicpatterns.com/uploads/qD9CTacyXTdwXXPtrCxZQw/MasterCard_Logo.png';
      case 'UnionPay':
        return 'https://cdn.magicpatterns.com/uploads/8TcgwChg7bMUnkD6TfzVkn/UnionPay_Logo.png';
      case 'PayPak':
        return 'https://cdn.magicpatterns.com/uploads/u4UiZ3PWX5VH4BCbbcVNsE/PayPak_Logo.png';
      default:
        return null;
    }
  };
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };
  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }
    return v;
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
    if (errors.cardNumber) setErrors({
      ...errors,
      cardNumber: ''
    });
  };
  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiry(formatExpiry(e.target.value));
    if (errors.expiry) setErrors({
      ...errors,
      expiry: ''
    });
  };
  // Luhn algorithm for card number validation
  const isValidCardNumber = (number: string): boolean => {
    const digits = number.replace(/\D/g, '');
    if (digits.length !== 16) return false;
    let sum = 0;
    let isEven = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      let digit = parseInt(digits[i], 10);
      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      isEven = !isEven;
    }
    return sum % 10 === 0;
  };
  const isValidExpiry = (value: string): boolean => {
    if (value.length < 5) return false;
    const [monthStr, yearStr] = value.split('/');
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);
    if (month < 1 || month > 12) return false;
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear() % 100;
    if (year < currentYear) return false;
    if (year === currentYear && month < currentMonth) return false;
    return true;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    const cleanCardNumber = cardNumber.replace(/\s/g, '');
    if (cleanCardNumber.length !== 16) {
      newErrors.cardNumber = 'Card number must be exactly 16 digits';
    } else if (!isValidCardNumber(cardNumber)) {
      newErrors.cardNumber = 'Please enter a valid card number';
    }
    if (!cardName.trim()) {
      newErrors.cardName = 'Name on card is required';
    } else if (cardName.trim().length > 30) {
      newErrors.cardName = 'Name must be 30 characters or less';
    }
    if (expiry.length < 5) {
      newErrors.expiry = 'Please enter a valid expiry date (MM/YY)';
    } else if (!isValidExpiry(expiry)) {
      newErrors.expiry = 'Card has expired. Enter a current or future date';
    }
    if (cvv.length !== 3) {
      newErrors.cvv = 'CVV must be exactly 3 digits';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSubmit({
      cardNumber,
      cardName,
      expiry,
      cvv
    });
  };
  const cardType = getCardType(cardNumber);
  const cardLogo = getCardLogo(cardType);
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-492" data-mp="oigj9swp31ux">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6 flex justify-between items-center" data-id="element-493" data-mp="ohx24eevchhy">
        <div data-id="element-494" data-mp="tf6yke0odtfa">
          <p className="text-sm text-blue-800 font-medium" data-id="element-495" data-mp="lxqxe4ifkn0c">Payment Amount</p>
          <p className="text-2xl font-bold text-blue-900" data-id="element-496" data-mp="lvoahtckr9zu">
            Rs. {totalAmount.toLocaleString()}
          </p>
        </div>
        <div className="text-right flex flex-col items-end" data-id="element-497" data-mp="bzb8872yrnth">
          <p className="text-xs text-blue-600 uppercase tracking-wider font-semibold mb-1" data-id="element-498" data-mp="bxqegjoh48wo">
            Gadgets Zone
          </p>
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-md shadow-sm border border-blue-100" data-id="element-499" data-mp="2ymrh4m6r9cw">
            <img src="https://cdn.magicpatterns.com/uploads/6qgM4mDpnqGm6ucRQC8GRZ/Gadgets_Zone_Logo.png" alt="Gadgets Zone" className="h-6 object-contain" data-id="element-500" data-mp="3ud0zvdq8fu8n" />
          </div>
        </div>
      </div>

      <div className="space-y-4" data-id="element-501" data-mp="3kfwgtmrooew2">
        <div data-id="element-502" data-mp="3kgxxc6rgxtl6">
          <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-503" data-mp="3kda0oyqq8r28">
            Card Number
          </label>
          <div className="relative" data-id="element-504" data-mp="3beya2wpv1t9s">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-505" data-mp="3bfzsjm97hi1j">
              <CreditCard className="h-5 w-5 text-gray-400" data-id="element-506" data-mp="30st8w1rx4pr4" />
            </div>
            <input type="text" id="cardNumber" value={cardNumber} onChange={handleCardNumberChange} maxLength={19} className={`block w-full pl-10 pr-12 py-3 border ${errors.cardNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors`} placeholder="0000 0000 0000 0000" data-id="element-507" data-mp="35sxs3zk7nyl1" />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-508" data-mp="3kjy3accce6kv">
              {cardLogo ? <img src={cardLogo} alt={cardType} className="h-6 object-contain" data-id="element-509" data-mp="3arsefbhe059w" /> : <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-1 rounded" data-id="element-510" data-mp="3arimhics7o1k">
                  Card
                </span>}
            </div>
          </div>
          {errors.cardNumber && <p className="mt-1 text-sm text-red-600" data-id="element-511" data-mp="78z0uyolzgfq">{errors.cardNumber}</p>}
        </div>

        <div data-id="element-512" data-mp="c338zl9m1uie">
          <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-513" data-mp="c3v6j1mrq2bb">
            Name on Card
          </label>
          <div className="relative" data-id="element-514" data-mp="hfih960va92i">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-515" data-mp="heh3gtsgv8h3">
              <User className="h-5 w-5 text-gray-400" data-id="element-516" data-mp="v5vot79ujlrb" />
            </div>
            <input type="text" id="cardName" value={cardName} onChange={e => {
            const val = e.target.value.slice(0, 30);
            setCardName(val);
            if (errors.cardName) setErrors({
              ...errors,
              cardName: ''
            });
          }} maxLength={30} className={`block w-full pl-10 py-3 border ${errors.cardName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors`} placeholder="Name on Card" data-id="element-517" data-mp="q70xfgxtcovl" />
          </div>
          {errors.cardName && <p className="mt-1 text-sm text-red-600" data-id="element-518" data-mp="1ivyczcan47kn">{errors.cardName}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-519" data-mp="1iy2hnwgd5alz">
          <div data-id="element-520" data-mp="3dd7tvsfd48uk">
            <label htmlFor="expiry" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-521" data-mp="3ddhojtbcr46l">
              Expiry Date
            </label>
            <div className="relative" data-id="element-522" data-mp="3hbfeiycy5ub1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-523" data-mp="3mbtl0bywgw6y">
                <Calendar className="h-5 w-5 text-gray-400" data-id="element-524" data-mp="3r4mokwimtyhg" />
              </div>
              <input type="text" id="expiry" value={expiry} onChange={handleExpiryChange} maxLength={5} className={`block w-full pl-10 py-3 border ${errors.expiry ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors`} placeholder="MM/YY" data-id="element-525" data-mp="3w37gm8nqdbut" />
            </div>
            {errors.expiry && <p className="mt-1 text-sm text-red-600" data-id="element-526" data-mp="duys9pbtgs6l">{errors.expiry}</p>}
          </div>

          <div data-id="element-527" data-mp="2ar5z6h73xuy7">
            <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-528" data-mp="2arfnm0ci7u1s">
              CVV
            </label>
            <div className="relative" data-id="element-529" data-mp="284n7ffq20zwo">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-530" data-mp="285opw59egoof">
                <Lock className="h-5 w-5 text-gray-400" data-id="element-531" data-mp="23clx3zcg5j9a" />
              </div>
              <input type="password" id="cvv" value={cvv} onChange={e => {
              setCvv(e.target.value.replace(/\D/g, '').slice(0, 3));
              if (errors.cvv) setErrors({
                ...errors,
                cvv: ''
              });
            }} maxLength={3} className={`block w-full pl-10 py-3 border ${errors.cvv ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors`} placeholder="•••" data-id="element-532" data-mp="1yg58z558z4w7" />
            </div>
            {errors.cvv && <p className="mt-1 text-sm text-red-600" data-id="element-533" data-mp="2d3ghey64u8s3">{errors.cvv}</p>}
          </div>
        </div>
      </div>

      <div className="pt-4 flex flex-col space-y-3" data-id="element-534" data-mp="15rr6od74lmeu">
        <button type="submit" className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-535" data-mp="11ol5hjyvelf8">
          Pay Rs. {totalAmount.toLocaleString()}
        </button>
        <button type="button" onClick={onCancel} className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-536" data-mp="2s216si33a9v2">
          Cancel and Return to Merchant
        </button>
      </div>

      <div className="text-center mt-4" data-id="element-537" data-mp="3ayj3ldwsozy6">
        <p className="text-xs text-gray-500 flex items-center justify-center" data-id="element-538" data-mp="3axhql3w7y3dt">
          <Lock className="h-3 w-3 mr-1" data-id="element-539" data-mp="3610r5djkk2lf" />
          Your payment information is encrypted and secure
        </p>
      </div>
    </form>;
}